/**
 * GlobalModals - Global modals that need to be shown throughout the app
 */

import { useAuth } from '@/contexts/AuthContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { WelcomeStampModal } from './WelcomeStampModal';
import { GuestNameModal } from './GuestNameModal';
import { OnboardingTour } from '@/components/onboarding/OnboardingTour';

export function GlobalModals() {
  const {
    user,
    showWelcomeModal,
    dismissWelcomeModal,
    emailVerified,
    showGuestNameModal,
    dismissGuestNameModal,
    updateGuestName,
  } = useAuth();
  const { shouldShowOnboarding, completeOnboarding } = useOnboarding();

  // Never interrupt a booking in progress with the tour: a guest who signs in
  // mid-flow would get the full-screen tutorial right after the name modal.
  // (window.location because GlobalModals mounts outside the router.)
  const onTransactionalRoute = ['/take-ticket', '/book-appointment'].some((p) =>
    window.location.pathname.startsWith(p)
  );

  // Modal precedence: GuestNameModal > OnboardingTour > WelcomeStampModal
  // The onboarding tour waits until the guest name modal is closed
  const showOnboardingTour =
    shouldShowOnboarding && !showGuestNameModal && !onTransactionalRoute;

  // Only show welcome modal to clients with verified email
  // Barbers, admins, and other non-client roles should never see it
  // It also waits until the onboarding tour has been completed/skipped
  const shouldShowWelcomeModal =
    showWelcomeModal && emailVerified && user?.role === 'client' && !showOnboardingTour;

  return (
    <>
      {/* Guest Name Modal - shown immediately after guest login */}
      <GuestNameModal
        isOpen={showGuestNameModal}
        onClose={dismissGuestNameModal}
        onSubmit={updateGuestName}
      />

      {/* Onboarding Tour - first-use welcome tutorial (shown once) */}
      <OnboardingTour isOpen={showOnboardingTour} onClose={completeOnboarding} />

      {/* Welcome Stamp Modal (replaces old Welcome Points Modal) */}
      <WelcomeStampModal
        isOpen={shouldShowWelcomeModal}
        onClose={dismissWelcomeModal}
        userName={user?.displayName || undefined}
      />
    </>
  );
}
